import React, { Fragment, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";

import AdministrationHeader from "../../components/header-components/AdministrationHeader.jsx";
import TableCard from "../../components/ui-components/TableCard";
import FlexTable from "../../components/ui-components/FlexTable";
import EmptyState from "../../components/ui-components/EmptyState";
import Modal from "../../components/ui-components/Modal";
import Badge from "../../components/ui-components/Badge";
import StatusPill from "../Budgets/components/StatusPill";
import { formatRelativeTime } from "../Budgets/formatters";
import {
  useApprovalPoliciesQuery,
  useDisableApprovalPolicyMutation,
} from "./hooks/useApprovalsQueries";
import { toast } from "../../components/ui-components/Toast";
import {
  formatApprovalPolicySummary,
  getApprovalPolicySummaryParts,
} from "./formatApprovalPolicySummary";
import { deriveHealthForPolicies } from "./deriveHealthForPolicies";

const STATUS_OPTIONS = [
  { value: "enabled", label: "Enabled" },
  { value: "disabled", label: "Disabled" },
];

function MetaChip({ children, className = "" }) {
  return (
    <span
      className={`inline-flex items-center whitespace-nowrap rounded-ctrl border border-border bg-surface-2 px-2 py-0.5 mt-1 text-xs text-text-2 ${className}`.trim()}
    >
      {children}
    </span>
  );
}

const OBJECT_TYPE_LABELS = {
  budget_period_version: "Budget period version",
  budget_creation: "Budget creation",
};

function formatObjectType(val) {
  return OBJECT_TYPE_LABELS[val] || val || "—";
}

function policyStatusKey(policy) {
  return policy.is_enabled ? "active" : "archived";
}

// Lowercase haystack across every rendered column so the search box matches broadly.
function policyHaystack(p) {
  const parts = getApprovalPolicySummaryParts(p);
  const priority = p.policy_json?.priority ?? p.priority;
  return [
    p.name,
    formatObjectType(p.object_type),
    typeof priority === "number" ? `P${priority}` : "",
    policyStatusKey(p),
    parts?.amountLabel,
    parts?.stepsLabel,
    parts?.tierCount != null ? `${parts.tierCount} tiers` : "",
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

// -- Inline action buttons (matches ReportList pattern) -----------------------

const editIcon = icon({ name: "edit", style: "light" });
const viewIcon = icon({ name: "eye", style: "light" });

function ActionButtons({ policy, onEdit, onDisable }) {
  return (
    <div className="flex items-center justify-center space-x-2">
      <button
        onClick={(e) => {
          e.stopPropagation();
          onEdit(policy.id);
        }}
        className="text-lg px-2 text-text-2 hover:text-text hover:bg-surface-2 rounded-ctrl transition-colors cursor-pointer"
        title={policy.is_enabled ? "Edit policy" : "View policy"}
      >
        <FontAwesomeIcon
          icon={policy.is_enabled ? editIcon : viewIcon}
          className="w-4 h-4"
        />
      </button>
      {policy.is_enabled && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDisable(policy);
          }}
          className="text-lg px-2 text-text-2 hover:text-danger hover:bg-danger-soft rounded-ctrl transition-colors cursor-pointer"
          title="Disable policy"
        >
          <FontAwesomeIcon
            icon={icon({ name: "circle-xmark", style: "light" })}
            className="w-4 h-4"
          />
        </button>
      )}
    </div>
  );
}

// -- Page component -----------------------------------------------------------

const ApprovalPoliciesPage = () => {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState("enabled");
  const [searchQuery, setSearchQuery] = useState("");
  const [disableModal, setDisableModal] = useState({ open: false, policy: null });

  const { data: policies, isLoading, error, refetch } = useApprovalPoliciesQuery(null);
  const disableMutation = useDisableApprovalPolicyMutation();

  const handleDisable = async () => {
    if (!disableModal.policy) return;
    try {
      await disableMutation.mutateAsync(disableModal.policy.id);
      toast.success("Policy disabled");
      setDisableModal({ open: false, policy: null });
    } catch (e) {
      // handled in mutation
    }
  };

  // Status filter first (Enabled / Disabled), then the search box on top — mirrors /budgets.
  const statusPolicies = useMemo(() => {
    const all = policies || [];
    if (statusFilter === "enabled") return all.filter((p) => p.is_enabled);
    if (statusFilter === "disabled") return all.filter((p) => !p.is_enabled);
    return all;
  }, [policies, statusFilter]);

  const search = searchQuery.trim();
  const searchLower = search.toLowerCase();
  const filteredPolicies = useMemo(
    () => (searchLower ? statusPolicies.filter((p) => policyHaystack(p).includes(searchLower)) : statusPolicies),
    [statusPolicies, searchLower]
  );

  const healthById = useMemo(
    () => deriveHealthForPolicies(filteredPolicies),
    [filteredPolicies]
  );

  // Contextual subtitle under the table title — mirrors the /budgets table hint.
  const matchCount = filteredPolicies.length;
  const noun = matchCount === 1 ? "policy" : "policies";
  const hint = isLoading
    ? "Loading…"
    : search
      ? `Showing ${matchCount} ${statusFilter} ${noun} matching "${search}"`
      : `Showing ${matchCount} ${statusFilter} ${noun}`;

  const columnDefs = useMemo(
    () => [
      {
        accessor: "name",
        Header: () => <span>Policy</span>,
        Cell: ({ row }) => {
          const policy = row.original;
          const { name } = policy;
          const health = healthById.get(String(policy.id));
          const needsAttention = health?.status === "warn";
          const parts = getApprovalPolicySummaryParts(policy);
          return (
            <div className="flex flex-col min-w-0">
              <div className="flex items-center gap-2 min-w-0">
                <span className="font-medium text-text truncate">{name || "Untitled"}</span>
                {needsAttention && (
                  <span
                    className="shrink-0"
                    title={health?.issueCount ? `Needs attention · ${health.issueCount}` : undefined}
                  >
                    <Badge status="needs attention" />
                  </span>
                )}
              </div>
              <div className="mt-1 flex min-w-0 items-center gap-1.5 overflow-hidden" title={formatApprovalPolicySummary(policy)}>
                <MetaChip className="shrink-0">{parts.tierCount} tiers</MetaChip>
                <MetaChip className="min-w-0 max-w-[180px] shrink">
                  <span className="block truncate">{parts.amountLabel}</span>
                </MetaChip>
                <MetaChip className="shrink-0">{parts.stepsLabel}</MetaChip>
              </div>
            </div>
          );
        },
        flex: 1,
        minWidth: 260,
      },
      {
        accessor: "object_type",
        Header: () => <span>Object type</span>,
        Cell: ({ value }) => (
          <span className="text-text-2 text-sm">{formatObjectType(value)}</span>
        ),
        width: 140,
        minWidth: 120,
        maxWidth: 180,
      },
      {
        accessor: (row) => row.policy_json?.priority ?? row.priority,
        id: "priority",
        align: "right",
        Header: () => <span>Priority</span>,
        Cell: ({ value }) =>
          typeof value === "number" ? (
            <span className="tabular-nums text-text-2">{`P${value}`}</span>
          ) : (
            <span className="text-text-2">—</span>
          ),
        width: 90,
        minWidth: 80,
        maxWidth: 110,
      },
      {
        accessor: "is_enabled",
        id: "status",
        align: "center",
        Header: () => <span>Status</span>,
        Cell: ({ row }) => <StatusPill status={policyStatusKey(row.original)} />,
        width: 120,
        minWidth: 110,
        maxWidth: 140,
      },
      {
        accessor: "updated_at",
        Header: () => <span>Updated</span>,
        Cell: ({ value }) => (
          <span
            className="text-text-2"
            title={value ? new Date(value).toISOString() : undefined}
          >
            {formatRelativeTime(value)}
          </span>
        ),
        width: 130,
        minWidth: 120,
        maxWidth: 160,
      },
      {
        id: "actions",
        align: "center",
        Header: () => null,
        Cell: ({ row }) => (
          <ActionButtons
            policy={row.original}
            onEdit={(id) => navigate(`/control-policies/approval-policies/${id}`)}
            onDisable={(p) => setDisableModal({ open: true, policy: p })}
          />
        ),
        width: 120,
        minWidth: 120,
        maxWidth: 160,
      },
    ],
    [navigate, healthById]
  );

  return (
    <Fragment>
      <AdministrationHeader title="Control Policies" showPeriodSelector={false} />

      <div className="w-full p-8">
        <TableCard
          title="Approval policies"
          subtitle={hint}
          primaryAction={{ label: "New Policy", icon: icon({ name: "plus" }), onClick: () => navigate("/control-policies/approval-policies/new") }}
          searchValue={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder="Search policies…"
          filterValue={statusFilter}
          onFilterChange={setStatusFilter}
          filterOptions={STATUS_OPTIONS}
          filterAriaLabel="Filter by status"
          isLoading={isLoading}
          error={error}
          errorMessage="Error loading policies"
          onRetry={refetch}
        >
          {statusPolicies.length === 0 ? (
            statusFilter === "disabled" ? (
              <EmptyState
                icon={icon({ name: "circle-xmark", style: "light" })}
                title="No disabled policies"
                message="Policies you disable will appear here."
              />
            ) : (
              <EmptyState
                icon={icon({ name: "scale-balanced", family: "sharp", style: "light" })}
                title="No approval policies yet"
                message="Create a policy to define approval requirements."
                action={{
                  label: "Create policy",
                  onClick: () => navigate("/control-policies/approval-policies/new"),
                  icon: icon({ name: "plus", style: "regular" }),
                }}
              />
            )
          ) : filteredPolicies.length === 0 ? (
            <EmptyState
              icon={icon({ name: "magnifying-glass" })}
              title="No matching policies"
              message={`No ${statusFilter} policies match "${search}".`}
              action={{
                label: "Clear search",
                onClick: () => setSearchQuery(""),
                icon: icon({ name: "xmark", style: "regular" }),
                variant: "secondary",
              }}
            />
          ) : (
            <FlexTable
              columns={columnDefs}
              data={filteredPolicies}
              getRowId={(p) => p.id}
              onRowClick={(id) => navigate(`/control-policies/approval-policies/${id}`)}
              rowAriaLabel={(p) => `View policy ${p.name}`}
            />
          )}
        </TableCard>
      </div>

      <Modal
        show={disableModal.open}
        close={() => setDisableModal({ open: false, policy: null })}
        title="Disable policy?"
        description="This policy will no longer require approval for new requests."
        confirmButton="Disable"
        cancelButton="Cancel"
        alertIcon
        confirmAction={handleDisable}
        icon={icon({ name: "circle-xmark", style: "solid" })}
      />
    </Fragment>
  );
};

export default ApprovalPoliciesPage;
